/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import getConfig from '../../config';
import { getLogger } from '../../utils/SearchLogger';
import uuid from 'uuid/v4';
import Request from '../../utils/Request';
import PerformanceUtils from '../../utils/PerformanceUtils';

export default async ({
	searchHistoryCount,
	searchHostContext,
}: {
	searchHistoryCount: number;
	searchHostContext: HostContext;
}): Promise<unknown> => {
	try {
		const hostname = getConfig(searchHostContext.environment, 'SEARCH_SERVER');
		const path = `/v1/users/me/searches?limit=${searchHistoryCount}`;
		const curRequestId = uuid();
		const startTime = Date.now();
		const headers = {
			'Content-Type': 'application/json',
			'x-api-key': searchHostContext.hostInfo.apiKey,
			authorization: `Bearer ${searchHostContext.imsInfo.accessToken}`,
			'x-request-id': curRequestId,
		};
		const historyResponse = await Request.withHeaders(headers).send('GET', `https://${hostname}${path}`);
		if (historyResponse.isError()) {
			const loggerData = {
				url: `https://${hostname}${path}`,
				status: historyResponse.status(),
				message: historyResponse.statusText(),
				requestId: curRequestId,
				duration: Date.now() - startTime,
				performanceTiming: PerformanceUtils.getPerformanceTimingByName(historyResponse.requestUrl()),
			};
			getLogger().error('Search Service', 'Problem with Search History fetch request', loggerData);
			const searchServiceError = new Error('fetchHistory error');
			throw searchServiceError;
		}
		return historyResponse.data();
	} catch (e) {
		getLogger().error('Search Service', 'Problem with Search History fetch request', e);
		const searchServiceError = new Error('fetchHistory error');
		throw searchServiceError;
	}
};
